import React, { Suspense, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthContextProvider } from './context/AuthContext';
import { ConfigProvider } from './context/ConfigContext';
import { DonationsProvider } from './context/DonationsContext';
import { AudioProvider } from './context/AudioContext';
import { Toaster } from 'react-hot-toast';
import App from './App';
import GamePage from './GamePage';
import DonatePage from './DonatePage';
import UserPage from './UserPage';
import LegalPage from './LegalPage';
import DynamicTitle from './components/DynamicTitle';
import NavigationProgressBar from './components/NavigationProgressBar';
import NotFoundPage from './NotFoundPage';
import './index.css';
import {
  fadeOutAndStopBackgroundMusic,
  setIsInGamePage,
} from './utils/soundUtils';

let lastPathWasGame = false;

const NavigationHandler: React.FC<{
  setLoading: (loading: boolean) => void;
}> = ({ setLoading }) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  React.useEffect(() => {
    setLoading(true);

    const isGamePage =
      location.pathname.match(/^\/[^/]+$/) &&
      !['/', '/donar', '/usuari', '/legal'].includes(location.pathname);
    const isOtherPage = ['/donar', '/usuari', '/legal', '/'].includes(
      location.pathname
    );

    const finishLoading = () => {
      setTimeout(() => {
        setLoading(false);
      }, 500);

      if (lastPathWasGame && !isGamePage) {
        fadeOutAndStopBackgroundMusic();
        setIsInGamePage(false);
      } else if (isOtherPage) {
        fadeOutAndStopBackgroundMusic();
        setIsInGamePage(false);
      }
    };

    if (lastPathWasGame && isGamePage) {
      setIsInGamePage(true);
      finishLoading();
    } else if (lastPathWasGame && !isGamePage) {
      setIsInGamePage(false);
      finishLoading();
    } else if (!lastPathWasGame && isGamePage) {
      setIsInGamePage(true);
      finishLoading();
    } else if (isOtherPage) {
      setIsInGamePage(false);
      finishLoading();
    } else {
      finishLoading();
    }

    lastPathWasGame = isGamePage;
  }, [location.pathname, navigationType, setLoading]);
  return null;
};

const RootComponent: React.FC = () => {
  const [pageLoading, setPageLoading] = useState(false);

  return (
    <React.StrictMode>
      <AuthContextProvider>
        <ConfigProvider>
          <ThemeProvider>
            <AudioProvider>
              <DonationsProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <NavigationHandler setLoading={setPageLoading} />
                  <DynamicTitle />
                  <NavigationProgressBar loading={pageLoading} />
                  <Suspense
                    fallback={
                      <div className="flex justify-center items-center h-screen">
                        Carregant...
                      </div>
                    }
                  >
                    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
                      <div className="bg-transition">
                        <Routes>
                          <Route path="/" element={<App />} />
                          <Route path="/donar" element={<DonatePage />} />
                          <Route path="/usuari" element={<UserPage />} />
                          <Route path="/legal" element={<LegalPage />} />
                          <Route path="/:path" element={<GamePage />} />
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </div>
                    </div>
                  </Suspense>
                </Router>
                <Toaster position="top-right" />
              </DonationsProvider>
            </AudioProvider>
          </ThemeProvider>
        </ConfigProvider>
      </AuthContextProvider>
    </React.StrictMode>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(<RootComponent />);
